import React, { useCallback, useEffect, useState, useRef, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapContainer, TileLayer, Circle, Polygon, Marker, Popup, Tooltip, useMap } from 'react-leaflet';
import { MapPin, Truck, Phone, Info, Search, Loader2, Navigation, CheckCircle2 } from 'lucide-react';
import L from 'leaflet';
// Fix for default marker icon in react-leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png'
});
const MILE_IN_METERS = 1609.34;
const CENTER: [number, number] = [47.3087, -122.0032]; // Black Diamond, WA
const zones = [{
  id: 4,
  radius: 23,
  price: '$150',
  label: '+$150',
  color: '#3b82f6',
  fillOpacity: 0
}, {
  id: 3,
  radius: 18,
  price: '$120',
  label: '+$120',
  color: '#3b82f6',
  fillOpacity: 0.118
}, {
  id: 2,
  radius: 13,
  price: '$75',
  label: '+$75',
  color: '#3b82f6',
  fillOpacity: 0.235
}, {
  id: 1,
  radius: 7,
  price: '$40',
  label: '+$40',
  color: '#3b82f6',
  fillOpacity: 0.353
}];
// Haversine formula to calculate distance in miles
function getDistanceMiles(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 3958.8; // Earth radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}
function getZoneForDistance(miles: number) {
  if (miles <= 7) return {
    zone: 1,
    price: '+$40',
    color: '#1c1d18'
  };
  if (miles <= 13) return {
    zone: 2,
    price: '+$75',
    color: '#8c9743'
  };
  if (miles <= 18) return {
    zone: 3,
    price: '+$120',
    color: '#e8d49e'
  };
  if (miles <= 23) return {
    zone: 4,
    price: '+$150',
    color: '#76cc26'
  };
  return {
    zone: 0,
    price: 'Custom',
    color: '#e8d49e'
  };
}
// Helper to generate circle coordinates as lat/lng array
function generateCircleCoords(center: [number, number], radiusMiles: number, points = 64): [number, number][] {
  const coords: [number, number][] = [];
  const radiusKm = radiusMiles * 1.60934;
  for (let i = 0; i < points; i++) {
    const angle = i / points * 2 * Math.PI;
    const lat = center[0] + radiusKm / 111.32 * Math.cos(angle);
    const lng = center[1] + radiusKm / (111.32 * Math.cos(center[0] * (Math.PI / 180))) * Math.sin(angle);
    coords.push([lat, lng]);
  }
  return coords;
}
// Component to fly the map to a location
function FlyTo({
  position
}: {
  position: [number, number] | null;
}) {
  const map = useMap();
  useEffect(() => {
    if (position) {
      map.flyTo(position, 11, {
        duration: 1.5
      });
    }
  }, [position, map]);
  return null;
}
// Ping data for random purchases
const PING_MATERIALS: {
  name: string;
  maxYards: number;
}[] = [{
  name: '5/8" Minus Gravel',
  maxYards: 12
}, {
  name: 'Pea Gravel',
  maxYards: 12
}, {
  name: '7/8" Drain Rock',
  maxYards: 12
}, {
  name: '3-Way Topsoil',
  maxYards: 15
}, {
  name: 'Medium Bark',
  maxYards: 15
}, {
  name: 'Coarse Bark',
  maxYards: 15
}, {
  name: '1.25" Minus',
  maxYards: 12
}, {
  name: 'Road Base',
  maxYards: 12
}, {
  name: 'Builders Sand',
  maxYards: 12
}, {
  name: 'Fill Dirt',
  maxYards: 15
}, {
  name: '2-4" Spalls',
  maxYards: 12
}];
const YARD_OPTIONS = [12, 15];
function getRandomQuantity(maxYards: number): string {
  const valid = YARD_OPTIONS.filter(y => y <= maxYards);
  return `${valid[Math.floor(Math.random() * valid.length)]} yards`;
}
function randomDateRecent(): string {
  const daysAgo = Math.floor(Math.random() * 7);
  if (daysAgo === 0) return 'Today';
  if (daysAgo === 1) return 'Yesterday';
  return `${daysAgo} days ago`;
}
// Generate a random lat/lng within a given mile radius ring from center
function randomPointInRing(center: [number, number], minMiles: number, maxMiles: number): [number, number] {
  const angle = Math.random() * 2 * Math.PI;
  const dist = minMiles + Math.random() * (maxMiles - minMiles);
  const distKm = dist * 1.60934;
  const lat = center[0] + distKm / 111.32 * Math.cos(angle);
  const lng = center[1] + distKm / (111.32 * Math.cos(center[0] * (Math.PI / 180))) * Math.sin(angle);
  return [lat, lng];
}
type PingData = {
  id: number;
  latLng: [number, number];
  material: string;
  quantity: string;
  date: string;
  createdAt: number;
};
// Radar Overlay Component
function RadarOverlay({
  center,
  isVisible
}: {
  center: [number, number];
  isVisible: boolean;
}) {
  const map = useMap();
  const [position, setPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [pings, setPings] = useState<PingData[]>([]);
  const pingIdRef = useRef(0);
  useEffect(() => {
    if (!isVisible) return;
    const updatePosition = () => {
      const point = map.latLngToContainerPoint(center);
      setPosition({
        x: point.x,
        y: point.y
      });
    };
    updatePosition();
    map.on('move', updatePosition);
    map.on('zoom', updatePosition);
    return () => {
      map.off('move', updatePosition);
      map.off('zoom', updatePosition);
    };
  }, [map, center, isVisible]);
  const [size, setSize] = useState(120);
  useEffect(() => {
    if (!isVisible) return;
    const updateSize = () => {
      const centerPoint = map.latLngToContainerPoint(center);
      const edgeLat = center[0] + 23 * 1.60934 / 111.32;
      const edgePoint = map.latLngToContainerPoint([edgeLat, center[1]]);
      const pixelRadius = Math.abs(centerPoint.y - edgePoint.y) * 2;
      setSize(pixelRadius);
    };
    updateSize();
    map.on('zoom', updateSize);
    map.on('zoomend', updateSize);
    return () => {
      map.off('zoom', updateSize);
      map.off('zoomend', updateSize);
    };
  }, [map, center, isVisible]);
  // Track radar angle and spawn pings when the sweep line crosses a queued position
  const radarStartRef = useRef(Date.now());
  const RADAR_DURATION = 13500; // matches CSS animation duration in ms
  const nextPingAngleRef = useRef<number>(Math.random() * 360);
  const lastSpawnAngleRef = useRef<number>(-1);
  useEffect(() => {
    if (!isVisible) return;
    radarStartRef.current = Date.now();
    const zoneRanges: [number, number][] = [[0, 7], [7, 13], [13, 18], [18, 23]];
    // Seed 3 beyond-zone-4 deliveries: 2 north, 1 south
    const beyondPings: PingData[] = [{
      id: pingIdRef.current++,
      latLng: (() => {
        const dist = 25 + Math.random() * 4;
        const angleRad = (350 + Math.random() * 20) * Math.PI / 180;
        const distKm = dist * 1.60934;
        return [center[0] + distKm / 111.32 * Math.cos(angleRad), center[1] + distKm / (111.32 * Math.cos(center[0] * (Math.PI / 180))) * Math.sin(angleRad)] as [number, number];
      })(),
      material: '5/8" Minus Gravel',
      quantity: '12 yards',
      date: 'Yesterday',
      createdAt: Date.now() - 500
    }, {
      id: pingIdRef.current++,
      latLng: (() => {
        const dist = 26 + Math.random() * 3;
        const angleRad = (10 + Math.random() * 30) * Math.PI / 180;
        const distKm = dist * 1.60934;
        return [center[0] + distKm / 111.32 * Math.cos(angleRad), center[1] + distKm / (111.32 * Math.cos(center[0] * (Math.PI / 180))) * Math.sin(angleRad)] as [number, number];
      })(),
      material: '3-Way Topsoil',
      quantity: '15 yards',
      date: '2 days ago',
      createdAt: Date.now() - 800
    }, {
      id: pingIdRef.current++,
      latLng: (() => {
        const dist = 25 + Math.random() * 5;
        const angleRad = (170 + Math.random() * 20) * Math.PI / 180;
        const distKm = dist * 1.60934;
        return [center[0] + distKm / 111.32 * Math.cos(angleRad), center[1] + distKm / (111.32 * Math.cos(center[0] * (Math.PI / 180))) * Math.sin(angleRad)] as [number, number];
      })(),
      material: 'Medium Bark',
      quantity: '15 yards',
      date: 'Today',
      createdAt: Date.now() - 300
    }];
    setPings(beyondPings);
    // Queue the first target angle
    nextPingAngleRef.current = Math.random() * 360;
    lastSpawnAngleRef.current = -1;
    const tick = setInterval(() => {
      const elapsed = (Date.now() - radarStartRef.current) % RADAR_DURATION;
      const currentAngle = elapsed / RADAR_DURATION * 360;
      const target = nextPingAngleRef.current;
      // Check if the radar arm just crossed the target angle
      const prev = lastSpawnAngleRef.current;
      const crossed = prev >= 0 && (prev < target && currentAngle >= target || prev > currentAngle && (prev < target || currentAngle >= target));
      lastSpawnAngleRef.current = currentAngle;
      if (crossed) {
        // Spawn ping at the target angle, random zone
        const zoneRange = zoneRanges[Math.floor(Math.random() * zoneRanges.length)];
        // Use the target angle for the ping position
        const dist = zoneRange[0] + Math.random() * (zoneRange[1] - zoneRange[0]);
        const distKm = dist * 1.60934;
        const angleRad = target * Math.PI / 180;
        const lat = center[0] + distKm / 111.32 * Math.cos(angleRad);
        const lng = center[1] + distKm / (111.32 * Math.cos(center[0] * (Math.PI / 180))) * Math.sin(angleRad);
        const mat = PING_MATERIALS[Math.floor(Math.random() * PING_MATERIALS.length)];
        const newPing: PingData = {
          id: pingIdRef.current++,
          latLng: [lat, lng],
          material: mat.name,
          quantity: getRandomQuantity(mat.maxYards),
          date: randomDateRecent(),
          createdAt: Date.now()
        };
        setPings(prev => [...prev.slice(-9), newPing]);
        // Queue next ping at a random angle offset (60-240 degrees ahead)
        nextPingAngleRef.current = (target + 60 + Math.random() * 180) % 360;
      }
    }, 100);
    return () => clearInterval(tick);
  }, [center, isVisible]);
  // Remove expired pings (older than 5 seconds), but keep seeded beyond-zone pings (id < 3)
  useEffect(() => {
    if (pings.length === 0) return;
    const timer = setInterval(() => {
      setPings(prev => prev.filter(p => p.id < 3 || Date.now() - p.createdAt < 5000));
    }, 500);
    return () => clearInterval(timer);
  }, [pings.length]);
  if (!isVisible || !position) return null;
  return <>
      {/* Radar sweep */}
      <div className="absolute pointer-events-none z-[400]" style={{
      left: position.x - size / 2,
      top: position.y - size / 2,
      width: size,
      height: size
    }} data-id="element-1187">
        <div className="relative w-full h-full rounded-full border border-brand-orange/15 bg-brand-orange/[0.02]" data-id="element-1188">
          <div className="radar-sweep" data-id="element-1189" />
          <div className="absolute top-1/2 left-1/2 w-2 h-2 -mt-1 -ml-1 bg-brand-orange/50 rounded-full shadow-[0_0_4px_rgba(5,137,77,0.4)]" data-id="element-1190" />
        </div>
      </div>

      {/* Purchase pings */}
      {pings.map(ping => {
      const point = map.latLngToContainerPoint(ping.latLng);
      const age = Date.now() - ping.createdAt;
      const opacity = age < 1000 ? age / 1000 : age > 3500 ? 1 - (age - 3500) / 1500 : 1;
      return <div key={ping.id} className="absolute pointer-events-none z-[450]" style={{
        left: point.x,
        top: point.y,
        transform: `translate(-50%, -100%) scale(${age < 1000 ? 0.8 + 0.2 * (age / 1000) : 1})`,
        opacity: Math.max(0, opacity),
        transition: 'opacity 0.5s ease, transform 0.5s ease'
      }} data-id="element-1191">
            {/* Ping dot */}
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-2 h-2 bg-brand-orange rounded-full shadow-[0_0_6px_rgba(5,137,77,0.6)]" data-id="element-1192" />
            {/* Info card */}
            <div className="mb-2 bg-black/70 backdrop-blur-md border border-white/10 rounded-lg px-3 py-2 whitespace-nowrap shadow-xl" data-id="element-1193">
              <p className="text-white text-xs font-semibold" data-id="element-1194">
                {ping.quantity} — {ping.material}
              </p>
              <p className="text-[10px]" style={{
            color: '#808080'
          }} data-id="element-1195">
                Purchased {ping.date}
              </p>
            </div>
          </div>;
    })}
    </>;
}
// Custom pulsating red marker for user location
const pulsatingIcon = L.divIcon({
  className: 'custom-div-icon',
  html: '<div class="pulsating-pin"><div class="pulsating-pin-dot"></div></div>',
  iconSize: [20, 20],
  iconAnchor: [10, 10],
  popupAnchor: [0, -15]
});
export function DeliveryZones() {
  const [address, setAddress] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [userPosition, setUserPosition] = useState<[number, number] | null>(null);
  const [searchResult, setSearchResult] = useState<{
    zone: number;
    price: string;
    color: string;
    distance: number;
    displayName: string;
  } | null>(null);
  const [searchError, setSearchError] = useState('');
  const hasAutoSearched = useRef(false);
  const handleSearch = useCallback(async () => {
    if (!address.trim()) return;
    setIsSearching(true);
    setSearchError('');
    setSearchResult(null);
    setUserPosition(null);
    try {
      const query = encodeURIComponent(address + ', WA');
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${query}&limit=1&countrycodes=us`);
      const data = await res.json();
      if (!data || data.length === 0) {
        setSearchError('Address not found. Please try a more specific address.');
        setIsSearching(false);
        return;
      }
      const lat = parseFloat(data[0].lat);
      const lon = parseFloat(data[0].lon);
      const distance = getDistanceMiles(CENTER[0], CENTER[1], lat, lon);
      const zoneInfo = getZoneForDistance(distance);
      setUserPosition([lat, lon]);
      setSearchResult({
        ...zoneInfo,
        distance: Math.round(distance * 10) / 10,
        displayName: data[0].display_name
      });
    } catch {
      setSearchError('Something went wrong. Please try again.');
    }
    setIsSearching(false);
  }, [address]);
  // Auto-search if address is passed via URL query param
  useEffect(() => {
    if (hasAutoSearched.current) return;
    // With HashRouter, query params are inside the hash: /#/delivery-zones?address=...
    const hash = window.location.hash; // e.g. "#/delivery-zones?address=123+Main"
    const qIndex = hash.indexOf('?');
    const params = qIndex >= 0 ? new URLSearchParams(hash.slice(qIndex)) : new URLSearchParams();
    const urlAddress = params.get('address');
    if (urlAddress) {
      setAddress(urlAddress);
      hasAutoSearched.current = true;
    }
  }, []);
  // Trigger search when address is set from URL param
  useEffect(() => {
    if (hasAutoSearched.current && address && !searchResult && !isSearching && !searchError) {
      handleSearch();
    }
  }, [address, hasAutoSearched.current]);
  return <main className="relative w-full min-h-[calc(100vh-64px)] flex flex-col lg:block bg-[#373737]" data-id="element-1196">
      {/* Single Map Container - Works at all screen sizes */}
      <div className="h-[50vh] lg:h-auto lg:absolute lg:inset-0 z-0" data-id="element-1197">
        <MapContainer center={CENTER} zoom={10} scrollWheelZoom={true} className="w-full h-full" zoomControl={false} data-id="element-1198">
          <TileLayer attribution="&copy; Google Maps" url="https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}" data-id="element-1199" />
          <FlyTo position={userPosition} data-id="element-1200" />

          <RadarOverlay center={CENTER} isVisible={!userPosition} data-id="element-1201" />

          {/* Blue overlay covering entire delivery area */}
          <Circle center={CENTER} radius={23 * MILE_IN_METERS} pathOptions={{
          color: 'transparent',
          stroke: false,
          fillColor: '#3b82f6',
          fillOpacity: 0.15,
          weight: 0
        }} data-id="element-1202" />

          {zones.map((zone, idx) => {
          const outerCoords = generateCircleCoords(CENTER, zone.radius);
          // Find the next smaller zone (inner boundary)
          const innerZone = zones[idx + 1];
          if (zone.id === 1) {
            // Zone 1: innermost circle
            return <Circle key={zone.id} center={CENTER} radius={zone.radius * MILE_IN_METERS} pathOptions={{
              color: 'transparent',
              stroke: false,
              fillColor: zone.color,
              fillOpacity: zone.fillOpacity,
              weight: 0
            }} data-id="element-1203">
                  <Popup data-id="element-1204">
                    <div className="text-center" data-id="element-1205">
                      <p className="font-bold text-brand-charcoal" data-id="element-1206">
                        Zone {zone.id}
                      </p>
                      <p className="font-bold text-brand-orange mt-1" data-id="element-1207">
                        Delivery: {zone.price}
                      </p>
                    </div>
                  </Popup>
                </Circle>;
          }
          // Zones 2-4: render as ring polygons (outer minus inner hole)
          const innerCoords = innerZone ? generateCircleCoords(CENTER, innerZone.radius).reverse() : [];
          return <Polygon key={zone.id} positions={innerCoords.length > 0 ? [outerCoords, innerCoords] : [outerCoords]} pathOptions={{
            color: 'transparent',
            stroke: false,
            fillColor: zone.color,
            fillOpacity: zone.fillOpacity,
            weight: 0
          }} data-id="element-1208">
                <Popup data-id="element-1209">
                  <div className="text-center" data-id="element-1210">
                    <p className="font-bold text-brand-charcoal" data-id="element-1211">
                      Zone {zone.id}
                    </p>
                    <p className="font-bold text-brand-orange mt-1" data-id="element-1212">
                      Delivery: {zone.price}
                    </p>
                  </div>
                </Popup>
              </Polygon>;
        })}

          {userPosition && <Marker position={userPosition} icon={pulsatingIcon} data-id="element-1213">
              {searchResult && <Tooltip permanent direction="right" offset={[12, 0]} className="zone-speech-bubble" data-id="element-1214">
                  <div className="flex items-center gap-2" data-id="element-1215">
                    <span className="font-bold text-white text-sm" data-id="element-1216">
                      {searchResult.zone > 0 ? `Zone ${searchResult.zone}` : 'Outside Zones'}
                    </span>
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-white/15 text-white/90" data-id="element-1217">
                      {searchResult.zone > 0 ? searchResult.price === 'FREE' ? 'Free Delivery' : `Delivery: ${searchResult.price}` : 'Call for Quote'}
                    </span>
                  </div>
                </Tooltip>}
              <Popup data-id="element-1218">
                <div className="text-center" data-id="element-1219">
                  <p className="font-bold text-brand-charcoal" data-id="element-1220">Your Location</p>
                  {searchResult && <p className="text-sm text-stone-600" data-id="element-1221">
                      Zone {searchResult.zone || 'Outside'}
                    </p>}
                </div>
              </Popup>
            </Marker>}
        </MapContainer>
      </div>

      {/* Top Gradient Overlay (Desktop) */}
      <div className="hidden lg:block absolute top-0 left-0 right-0 h-64 bg-gradient-to-b from-[#373737]/90 via-[#373737]/50 to-transparent z-10 pointer-events-none" data-id="element-1222" />

      {/* Floating UI Container */}
      <div className="relative z-20 flex-1 flex flex-col lg:block lg:absolute lg:inset-0 lg:pointer-events-none" data-id="element-1223">
        {/* Header & Search (Top Center) */}
        <div className="w-full max-w-3xl mx-auto px-4 pt-8 lg:pt-16 pointer-events-auto" data-id="element-1224">
          <motion.div initial={{
          opacity: 0,
          y: -20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-center mb-8" data-id="element-1225">
            <h1 className="font-heading text-4xl lg:text-5xl font-extrabold text-white mb-3 tracking-tight drop-shadow-lg" data-id="element-1226">
              Delivery <span className="text-brand-orange" data-id="element-1227">Zones</span>
            </h1>
            <p className="text-lg text-stone-300 font-medium drop-shadow-md max-w-xl mx-auto font-sub" data-id="element-1228">
              Enter your address to instantly calculate your delivery zone and
              cost.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} data-id="element-1229">
            <form onSubmit={e => {
            e.preventDefault();
            handleSearch();
          }} className="flex flex-col sm:flex-row bg-[#474747]/95 backdrop-blur-md border border-brand-orange shadow-2xl rounded-[5px]" data-id="element-1230">
              <div className="flex-1 relative" data-id="element-1231">
                <Navigation className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" data-id="element-1232" />
                <input type="text" value={address} onChange={e => setAddress(e.target.value)} placeholder="Enter your delivery address..." autoComplete="street-address" className="w-full pl-14 pr-4 py-4 bg-transparent text-white placeholder-stone-400 focus:outline-none text-lg border-none focus:ring-0 transition-colors" data-id="element-1233" />
              </div>
              <button type="submit" disabled={isSearching || !address.trim()} className="px-8 py-4 bg-brand-button hover:bg-brand-buttonHover disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold transition-all flex items-center justify-center gap-2 shrink-0 border-l border-brand-orange" data-id="element-1234">
                {isSearching ? <Loader2 className="w-5 h-5 animate-spin" data-id="element-1235" /> : <Search className="w-5 h-5" data-id="element-1236" />}
                <span data-id="element-1237">Find Zone</span>
              </button>
            </form>

            {/* Search Result */}
            <AnimatePresence mode="wait" data-id="element-1238">
              {searchError && <motion.div initial={{
              opacity: 0,
              y: -10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              scale: 0.95
            }} className="mt-4 p-4 bg-red-500/20 backdrop-blur-md border border-red-500/30 rounded-2xl text-red-200 text-center text-sm font-medium" data-id="element-1239">
                  {searchError}
                </motion.div>}
            </AnimatePresence>
          </motion.div>
        </div>

        {/* Legend (Bottom Left Desktop, Flow Mobile) */}
        <div className="w-full lg:fixed lg:top-[143px] lg:left-8 lg:w-auto p-4 lg:p-0 pointer-events-auto lg:z-40" data-id="element-1240">
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.3
        }} className="bg-[#474747]/95 backdrop-blur-xl rounded-3xl p-6 border border-white/10 shadow-2xl w-full lg:w-[340px]" data-id="element-1241">
            <h2 className="font-sans text-lg font-bold text-white mb-4 flex items-center" data-id="element-1242">
              <Truck className="w-5 h-5 mr-2 text-brand-orange" data-id="element-1243" />
              Delivery Pricing
            </h2>

            <div className="space-y-2" data-id="element-1244">
              {/* Zone 1 */}
              <div className="flex items-center justify-between py-2.5 px-3 rounded-lg hover:bg-white/5 transition-colors" data-id="element-1245">
                <div className="flex items-center" data-id="element-1246">
                  <div className="w-2.5 h-2.5 rounded-full bg-white/40 mr-3" data-id="element-1247" />
                  <p className="font-medium text-stone-200 text-sm" data-id="element-1248">Zone 1</p>
                </div>
                <span className="text-stone-300 text-xs font-medium" data-id="element-1249">+$40</span>
              </div>

              {/* Zone 2 */}
              <div className="flex items-center justify-between py-2.5 px-3 rounded-lg hover:bg-white/5 transition-colors" data-id="element-1250">
                <div className="flex items-center" data-id="element-1251">
                  <div className="w-2.5 h-2.5 rounded-full bg-white/30 mr-3" data-id="element-1252" />
                  <p className="font-medium text-stone-200 text-sm" data-id="element-1253">Zone 2</p>
                </div>
                <span className="text-stone-300 text-xs font-medium" data-id="element-1254">+$75</span>
              </div>

              {/* Zone 3 */}
              <div className="flex items-center justify-between py-2.5 px-3 rounded-lg hover:bg-white/5 transition-colors" data-id="element-1255">
                <div className="flex items-center" data-id="element-1256">
                  <div className="w-2.5 h-2.5 rounded-full bg-white/20 mr-3" data-id="element-1257" />
                  <p className="font-medium text-stone-200 text-sm" data-id="element-1258">Zone 3</p>
                </div>
                <span className="text-stone-300 text-xs font-medium" data-id="element-1259">
                  +$120
                </span>
              </div>

              {/* Zone 4 */}
              <div className="flex items-center justify-between py-2.5 px-3 rounded-lg hover:bg-white/5 transition-colors" data-id="element-1260">
                <div className="flex items-center" data-id="element-1261">
                  <div className="w-2.5 h-2.5 rounded-full bg-white/10 mr-3" data-id="element-1262" />
                  <p className="font-medium text-stone-200 text-sm" data-id="element-1263">Zone 4</p>
                </div>
                <span className="text-stone-300 text-xs font-medium" data-id="element-1264">
                  +$150
                </span>
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-white/10" data-id="element-1265">
              <div className="flex items-start" data-id="element-1266">
                <Info className="w-4 h-4 text-brand-orange mr-2 shrink-0 mt-0.5" data-id="element-1267" />
                <div data-id="element-1268">
                  <p className="text-xs text-stone-300" data-id="element-1269">
                    Outside Zone 4? We can still deliver. Call{' '}
                    <a href="tel:206-619-3742" className="text-brand-orange font-bold hover:underline" data-id="element-1270">
                      (206) 619-3742
                    </a>{' '}
                    for a custom quote.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </main>;
}